import { createFileRoute, Link } from "@tanstack/react-router";
import { getSizeSeed } from "@/lib/size-compare.functions";
import { SizeComparison } from "@/components/SizeComparison";
import type { SizeEntry } from "@/lib/size-compare.server";
import { breadcrumbLd, faqLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";

const TITLE = "Car Size Comparison Tool: Compare Vehicle Dimensions";
const DESC =
  "Car size comparison tool: compare any two vehicles to scale. Overlay real length, width, height and wheelbase figures for 400+ models and see exactly how much bigger one car is than the other.";
const ANSWER =
  "Pick two vehicles and this tool draws both to scale from their published length, width, height and wheelbase, then lists the exact difference in each dimension. A full-size SUV is typically 15-25 inches longer and 5-8 inches wider than a midsize sedan, which is what decides whether it fits a standard 16-foot garage bay.";

const SIZE_CLASSES: { name: string; length: string; width: string; height: string }[] = [
  { name: "Subcompact car", length: "160–174 in", width: "66–70 in", height: "57–61 in" },
  { name: "Compact car", length: "175–186 in", width: "70–73 in", height: "56–59 in" },
  { name: "Midsize sedan", length: "192–196 in", width: "72–74 in", height: "56–58 in" },
  { name: "Compact SUV", length: "175–184 in", width: "72–75 in", height: "63–67 in" },
  { name: "Midsize 3-row SUV", length: "196–204 in", width: "76–79 in", height: "68–71 in" },
  { name: "Full-size SUV", length: "203–226 in", width: "79–81 in", height: "75–77 in" },
  { name: "Midsize pickup", length: "210–225 in", width: "74–78 in", height: "70–74 in" },
  { name: "Full-size pickup", length: "232–250 in", width: "80–84 in", height: "75–81 in" },
  { name: "Minivan", length: "200–205 in", width: "77–79 in", height: "68–70 in" },
];

const FAQS = [
  {
    q: "How do I compare the size of two cars?",
    a: "Compare four figures: overall length, overall width (excluding mirrors), overall height and wheelbase. Length and width decide whether the car fits your garage or parking space, height decides clearance, and wheelbase drives ride quality and interior room. This tool overlays both vehicles to scale so the difference is visible, not just numeric.",
  },
  {
    q: "What size car fits in a standard garage?",
    a: "A single-car garage bay is usually 16 feet (192 in) deep and 9-10 feet (108-120 in) wide. Leave at least 24 inches of length and 30 inches of total width clearance for doors, so a vehicle up to roughly 200 inches long and 80 inches wide fits comfortably in a typical bay.",
  },
  {
    q: "Is vehicle width measured with or without mirrors?",
    a: "Published width figures are body width excluding mirrors. Mirrors typically add 8-12 inches to the total, which matters for narrow garages, car washes and lane positioning. Add that margin manually when planning tight clearances.",
  },
  {
    q: "Why does wheelbase matter more than length?",
    a: "Wheelbase is the distance between the front and rear axles, and it determines how much of the vehicle's length is usable cabin space. Two cars with the same overall length can have very different rear legroom if one has longer overhangs and a shorter wheelbase.",
  },
];

export const Route = createFileRoute("/car-size-comparison")({
  validateSearch: (search: Record<string, unknown>) => ({
    a: typeof search['a'] === "string" ? search['a'] : undefined,
    b: typeof search['b'] === "string" ? search['b'] : undefined,
  }),
  loaderDeps: ({ search }) => ({ a: search.a, b: search.b }),
  loader: async ({ deps }) => await getSizeSeed({ data: { ...(deps.a ? { a: deps.a } : {}), ...(deps.b ? { b: deps.b } : {}) } }),
  head: () => ({
    meta: [
      { title: pageTitle(TITLE) },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://autospecdata.com/car-size-comparison" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://autospecdata.com/car-size-comparison" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Car Size Comparison Tool",
          url: "https://autospecdata.com/car-size-comparison",
          description: DESC,
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "[data-quick-answer]"],
          },
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Car Size Comparison", path: "/car-size-comparison" },
          ]),
        ),
      },
    ],
  }),
  component: SizeComparisonPage,
});

function SizeComparisonPage() {
  const entries = (Route.useLoaderData() ?? []) as SizeEntry[];
  const { a, b } = Route.useSearch();

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 space-y-8">
      <nav className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">Car Size Comparison</span>
      </nav>

      <header className="space-y-3">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Car Size Comparison: Vehicle Size &amp; Car Dimension Comparison
        </h1>
        <p data-quick-answer className="text-muted-foreground leading-relaxed">
          {ANSWER}
        </p>
      </header>

      <SizeComparison
        entries={entries}
        {...(a ? { initialA: a } : {})}
        {...(b ? { initialB: b } : {})}
      />

      <section className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">
          How to read this vehicle size comparison
        </h2>
        <dl className="space-y-3 text-sm leading-relaxed">
          <div>
            <dt className="font-medium">Overall length</dt>
            <dd className="text-muted-foreground">
              Bumper to bumper. The figure that decides garage and parallel-parking fit.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Overall width</dt>
            <dd className="text-muted-foreground">
              Body width, mirrors folded or excluded. Add 8-12 inches for extended mirrors.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Overall height</dt>
            <dd className="text-muted-foreground">
              Roof to ground at curb weight. Check it against parking-structure clearance,
              usually 6 ft 8 in (80 in).
            </dd>
          </div>
          <div>
            <dt className="font-medium">Wheelbase</dt>
            <dd className="text-muted-foreground">
              Front axle to rear axle. Longer wheelbase generally means more cabin room and a
              steadier ride; shorter means a tighter turning circle.
            </dd>
          </div>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">
          Typical car sizes by class
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Most size questions resolve at the class level before you get to a specific
          model. These are the working ranges across current US-market vehicles — length
          and width measured bumper to bumper and body-side to body-side, mirrors
          excluded.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Typical vehicle length, width and height by class</caption>
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="py-2 pr-4 font-medium">Class</th>
                <th scope="col" className="py-2 pr-4 font-medium">Length</th>
                <th scope="col" className="py-2 pr-4 font-medium">Width</th>
                <th scope="col" className="py-2 font-medium">Height</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              {SIZE_CLASSES.map((c) => (
                <tr key={c.name} className="border-b border-border/60">
                  <th scope="row" className="py-2 pr-4 text-left font-normal text-foreground">
                    {c.name}
                  </th>
                  <td className="py-2 pr-4 tabular-nums">{c.length}</td>
                  <td className="py-2 pr-4 tabular-nums">{c.width}</td>
                  <td className="py-2 tabular-nums">{c.height}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">
          Which dimension actually decides the answer
        </h2>
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground max-w-3xl">
          <p>
            <span className="font-medium text-foreground">Garage depth is the usual
            binding constraint.</span> A standard single bay is 192 inches deep. Subtract
            24 inches for walking room at the nose and clearance for the door track and
            you have roughly 200 inches of usable vehicle length before the door stops
            closing. That is why a midsize sedan at 192–196 inches fits everywhere and a
            full-size three-row SUV at 203–214 inches does not. Extended-cab and
            crew-cab pickups at 232–250 inches clear almost no residential bay at all.
          </p>
          <p>
            <span className="font-medium text-foreground">Width fails second, and it
            fails on mirrors.</span> Published width excludes mirrors, and mirrors add
            8–12 inches. A 79-inch-wide SUV is closer to 89 inches with mirrors out —
            wider than the 8-foot 6-inch lane of most automatic car washes and enough to
            make a 108-inch bay unusable once you need to open a door. Compare
            mirror-out width, not spec-sheet width, whenever the space is tight on both
            sides.
          </p>
          <p>
            <span className="font-medium text-foreground">Height only matters until it
            doesn't.</span> Parking-structure clearance is typically 80 inches. Almost
            every car, crossover and midsize SUV clears it. Full-size body-on-frame SUVs,
            lifted trucks and any vehicle with a roof box or crossbars do not — a
            roof-mounted cargo box adds 15–20 inches, which puts a 71-inch crossover at
            roughly 90 inches and into the garage-entrance strike zone.
          </p>
          <p>
            <span className="font-medium text-foreground">Wheelbase predicts interior
            space better than length.</span> Two vehicles with identical overall length
            can differ by 8 inches of rear legroom because one spends its length on
            overhangs. As a rule, wheelbase above 112 inches means usable adult rear
            seating, above 120 inches means a genuinely comfortable third row, and below
            105 inches means a tight back seat regardless of how long the car looks from
            outside. Wheelbase also sets turning circle: shorter is easier in a city
            garage, longer is steadier at highway speed.
          </p>
          <p>
            <span className="font-medium text-foreground">Curb weight is the hidden size
            dimension.</span> Two SUVs within an inch of each other can be 900 pounds
            apart, and that gap shows up in braking distance, tire wear, fuel economy and
            whether a residential driveway or older parking deck is rated for it. It also
            determines towing math: your tow vehicle's weight, not its length, is what
            keeps a trailer stable. If you're cross-shopping on size, read weight
            alongside length rather than after it.
          </p>
        </div>
      </section>



      <section className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">
          Frequently asked questions
        </h2>
        <dl className="space-y-4">
          {FAQS.map((f) => (
            <div key={f.q} className="rounded-lg border border-border p-4">
              <dt className="font-medium">{f.q}</dt>
              <dd className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">Related tools</h2>
        <ul className="grid gap-2 sm:grid-cols-2 text-sm">
          <li>
            <Link to="/compare" className="cta-gold">
              Compare vehicle specs & MPG
            </Link>
          </li>
          <li>
            <Link to="/tools" className="text-primary hover:underline">
              Free car calculators
            </Link>
          </li>
          <li>
            <Link
              to="/search"
              search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
              className="text-primary hover:underline"
            >
              Search all vehicle specifications
            </Link>
          </li>
          <li>
            <Link to="/vin-decoder" className="text-primary hover:underline">
              Free VIN decoder
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}